import {
    Group,
    Money,
    WarningAmber,
    Assessment
} from "@mui/icons-material";
import {
    Button,
    Paper,
    Box,
    Typography
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getAccountsList, getCustomersList, getPreferences } from "../firebase/dataHandler";
import { requestNotificationPermission } from "../firebase/pushNotifications";
import { app } from "../firebase/authHandler";

import PremiumFeatureLock from "../components/PremiumFeatureLock";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import { useTranslation } from 'react-i18next';

function Dashboard() {
  const [customers, setCustomers] = useState(null);
  const [accounts, setAccounts] = useState(null);
  const [useHandedAmount, setUseHandedAmount] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    getPreferences().then(prefs => {
      setUseHandedAmount(prefs.useHandedAmountForAnalytics || false);
    }).catch(console.error);

    // Request push notification permission
    setTimeout(() => {
      requestNotificationPermission(app);
    }, 2000);
  }, []);

  useEffect(() => {
    getAccountsList()
      .then((accountList) => {
        setAccounts({
          list: accountList,
          length: Object.values(accountList).filter(a=>a.isActive).length,
        });
      })
      .catch((e) => {
        alert(e);
      });

    getCustomersList()
      .then((customerList) => {
        setCustomers({
          list: customerList,
          length: Object.values(customerList).filter(c=>c.isActive).length,
        });
      })
      .catch((e) => {
        alert(e);
      });
  }, []);


  const getAllEntries = (accounts) => {
    let entries = accounts.filter(a=> a.isActive || a.isActive === undefined).map(({entries, name, interestPercentage, accountId, installmentAmount})=>entries && Object.values(entries).map(entry=>{return {accountId: accountId.split("_")[0], installmentAmount, name, date: entry.date, amount : entry.amountPaid, interestPercentage, profitAmount :  entry.isUpfrontInterest ? entry.amountPaid : entry.amountPaid * (interestPercentage/100) , dayCount : entry.dayCount, isUpfrontInterest: entry.isUpfrontInterest}})).filter(n=>n)
    return [].concat(...entries).sort((a,b)=> new Date(a.date)-new Date(b.date))
  }

  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();

  const thisMothCollectedAmount = useMemo(() => {
    if (!accounts) return 0;
    return Object.values(accounts.list)
      .filter(a => a.isActive || a.isActive === undefined)
      .reduce((totalAcc, acc) => {
        if (!acc.entries) return totalAcc;
        const accountPaidSum = Object.values(acc.entries)
          .filter(e => new Date(e.date).getMonth() === currentMonth && new Date(e.date).getFullYear() === currentYear && !e.isUpfrontInterest)
          .reduce((sum, entry) => sum + Number(entry.amountPaid || 0), 0);
        return totalAcc + accountPaidSum;
      }, 0);
  }, [accounts, currentMonth, currentYear]);

  const thisMothUpfrontCollected = useMemo(() => {
    if (!accounts) return 0;
    return Object.values(accounts.list)
      .filter(a => a.isActive || a.isActive === undefined)
      .reduce((totalAcc, acc) => {
        if (!acc.entries) return totalAcc;
        const accountPaidSum = Object.values(acc.entries)
          .filter(e => new Date(e.date).getMonth() === currentMonth && new Date(e.date).getFullYear() === currentYear && e.isUpfrontInterest)
          .reduce((sum, entry) => sum + Number(entry.amountPaid || 0), 0);
        return totalAcc + accountPaidSum;
      }, 0);
  }, [accounts, currentMonth, currentYear]);

  const thisMonthInvestment = useMemo(() => {
    return accounts ? Object.values(accounts.list).filter(a=>a.isActive || a.isActive === undefined).filter(({date})=> new Date(date).getMonth() === currentMonth && new Date(date).getFullYear() === currentYear ).reduce((acc, sum)=> acc + (useHandedAmount ? Number(sum.amount || 0) - Number(sum.interestAmount || 0) : Number(sum.amount || 0)), 0) : 0
  }, [accounts, currentMonth, currentYear, useHandedAmount])

  const thisMonthProfit = useMemo(() => {
    return accounts ? Object.values(accounts.list).filter(a=>a.isActive || a.isActive === undefined).filter(({date})=> new Date(date).getMonth() === currentMonth && new Date(date).getFullYear() === currentYear ).reduce((acc, {interestAmount})=> acc + Number(interestAmount || 0), 0) : 0
  }, [accounts, currentMonth, currentYear])

  const totalProfitCollected = useMemo(() => {
    return accounts ? getAllEntries(Object.values(accounts.list)).reduce((acc, {profitAmount})=> acc + Number(profitAmount || 0), 0) : 0
  }, [accounts])

  const totalInvested = useMemo(() => {
    return accounts ? Object.values(accounts.list).filter(a => a.isActive || a.isActive === undefined).reduce((acc, sum) => acc + (useHandedAmount ? Number(sum.amount || 0) - Number(sum.interestAmount || 0) : Number(sum.amount || 0)), 0) : 0
  }, [accounts, useHandedAmount])

  const totalReturned = useMemo(() => {
    return accounts ? getAllEntries(Object.values(accounts.list)).reduce((acc, {amount, isUpfrontInterest})=> acc + (isUpfrontInterest ? 0 : Number(amount || 0)), 0) : 0
  }, [accounts])

  const totalUpfrontReturned = useMemo(() => {
    return accounts ? getAllEntries(Object.values(accounts.list)).reduce((acc, {amount, isUpfrontInterest})=> acc + (isUpfrontInterest ? Number(amount || 0) : 0), 0) : 0
  }, [accounts])

  const modeAnalytics = useMemo(() => {
    let data = { daily: { c: 0, est: 0 }, weekly: { c: 0, est: 0 }, monthly: { c: 0, est: 0 }, notTallied: 0 };
    if (!accounts) return data;

    Object.values(accounts.list).filter(a => a.isActive !== false).forEach(a => {
      const mode = a.installmentType || 'daily';
      if (mode === 'daily') {
        data.daily.c++;
        data.daily.est += Number(a.installmentAmount || 0);
      } else if (mode === 'weekly') {
        data.weekly.c++;
        data.weekly.est += Number(a.installmentAmount || 0);
      } else if (mode === 'monthly') {
        data.monthly.c++;
        data.monthly.est += Number(a.installmentAmount || 0);
      }

      const paidAmount = a.entries ? Object.values(a.entries).reduce((acc, e) => acc + (e.isUpfrontInterest ? 0 : Number(e.amountPaid || 0)), 0) : 0;
      const paidDays = a.entries ? Object.values(a.entries).reduce((acc, e) => acc + (e.isUpfrontInterest ? 0 : Number(e.dayCount || 0)), 0) : 0;
      const expectedAmount = paidDays * Number(a.installmentAmount || 0);
      if (paidAmount !== expectedAmount) {
        data.notTallied++;
      }
    });
    return data;
  }, [accounts]);


  // Analytics for Charts
  const monthlyTrackingData = useMemo(() => {
    if (!accounts) return [];
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    
    let data = [];
    let expectedDaily = modeAnalytics.daily.est + (modeAnalytics.weekly.est / 7);
    
    for (let i = 1; i <= daysInMonth; i++) {
      let actualForDay = 0;
      let targetDateStr = new Date(currentYear, currentMonth, i).toDateString();
      
      Object.values(accounts.list).forEach(a => {
        if (a.entries) {
          Object.values(a.entries).forEach(e => {
            if (new Date(e.date).toDateString() === targetDateStr) {
              actualForDay += Number(e.amountPaid || 0);
            }
          });
        }
      });

      if (new Date(currentYear, currentMonth, i) > new Date() && actualForDay === 0) {
        // Don't plot 0 for future dates
        actualForDay = null;
      }

      data.push({
        day: i,
        Estimated: Math.round(expectedDaily),
        Actual: actualForDay
      });
    }
    return data;
  }, [accounts, currentMonth, currentYear, modeAnalytics]);

  const portfolioDistribution = useMemo(() => {
    if (!accounts) return [];
    return [
      { name: 'Daily', value: modeAnalytics.daily.c },
      { name: 'Weekly', value: modeAnalytics.weekly.c },
      { name: 'Monthly', value: modeAnalytics.monthly.c }
    ].filter(d => d.value > 0);
  }, [accounts, modeAnalytics]);
  const PIE_COLORS = ['#38bdf8', '#c084fc', '#fb923c'];

  const recoveryData = useMemo(() => {
    if (!totalInvested || !totalReturned) return [];
    return [
      { name: 'Portfolio Summary', Invested: totalInvested, Recovered: totalReturned }
    ];
  }, [totalInvested, totalReturned]);

  const topDefaulters = useMemo(() => {
    if (!accounts) return [];
    const today = new Date();
    const defaulters = Object.values(accounts.list).filter(a => a.isActive !== false).map(a => {
      let paidDayCount = a.entries ? Object.values(a.entries).reduce((acc, e) => acc + (e.isUpfrontInterest ? 0 : Number(e.dayCount || 0)), 0) : 0;
      
      let elapsedDays = Math.round((today.getTime() - new Date(a.date).getTime()) / 86400000);
      let expectedDays = 0;
      if (a.installmentType === 'daily') expectedDays = elapsedDays;
      else if (a.installmentType === 'weekly') expectedDays = Math.floor(elapsedDays / 7);
      else if (a.installmentType === 'monthly') expectedDays = Math.floor(elapsedDays / 30);
  
      expectedDays = Math.min(expectedDays, a.installmentPeriod);
      const unpaidPeriods = expectedDays - paidDayCount;
      return { ...a, unpaidPeriods, paidDayCount };
    }).filter(a => a.unpaidPeriods > 0)
      .sort((a, b) => b.unpaidPeriods - a.unpaidPeriods)
      .slice(0, 5);
    return defaulters;
  }, [accounts]);


  return (
    <div className="page-pad fade-in">

      {/* ── Header ──────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 4px 16px' }}>
        <div>
          <h1 style={{ fontSize: 28, marginBottom: 4 }}>Dashboard</h1>
          <small style={{ color: 'var(--text-muted)', fontSize: 12 }}>Financial overview</small>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {customers && (
            <span className="stat-badge blue" title="Active Customers">
              <Group style={{ fontSize: 14 }} />
              {customers.length}
            </span>
          )}
          {accounts && (
            <span className="stat-badge teal" title="Active Accounts">
              <Money style={{ fontSize: 14 }} />
              {accounts.length}
            </span>
          )}
          <Button 
            variant="outlined" 
            size="small" 
            startIcon={<Assessment />}
            onClick={() => navigate('/reports')}
            sx={{
              borderColor: 'var(--accent-blue)', color: 'var(--accent-blue)', borderRadius: 2,
              textTransform: 'none', fontFamily: 'Inter', fontSize: 12, fontWeight: 600, padding: '4px 12px'
            }}
          >
            Go to Reports
          </Button>
        </div>
      </div>

      <PremiumFeatureLock 
        title="Dashboard Analytics"
        description="Track your business growth with real-time analytics and visualizations."
        features={[
          'Daily collection & recovery trends',
          'Identify top-performing areas',
          'Detailed cash flow insights',
          'Export metrics for reporting'
        ]}
      >
      {/* ── Stat Cards Grid ───────────────────────── */}
      <div className="dashboard-cards-grid">
        <DashboardCard
          title={t('dashboard.this_month')}
          subheader={t('dashboard.disbursed_interest_payable')}
          accentColor="var(--accent-blue)"
          data={accounts && `₹ ${thisMonthInvestment.toLocaleString('en-IN', { maximumFractionDigits: 3 })}`}
          subdata={accounts && `₹ ${thisMonthProfit.toLocaleString('en-IN', { maximumFractionDigits: 3 })} ${t('dashboard.interest')}`}
        />
        <DashboardCard
          title={t('dashboard.recovery')}
          subheader={t('dashboard.recovered_this_month')}
          accentColor="var(--accent-green)"
          data={accounts && `₹ ${(thisMothCollectedAmount + thisMothUpfrontCollected).toLocaleString('en-IN', { maximumFractionDigits: 3 })}`}
          subdata={accounts && (thisMothUpfrontCollected > 0 ? `₹ ${thisMothCollectedAmount.toLocaleString('en-IN', { maximumFractionDigits: 3 })} ${t('dashboard.installs')} + ₹ ${thisMothUpfrontCollected.toLocaleString('en-IN', { maximumFractionDigits: 3 })} ${t('dashboard.upfront')}` : t('dashboard.installment_collections'))}
        />
        <DashboardCard
          title={t('dashboard.overall_portfolio')}
          subheader={t('dashboard.total_disbursed_interest_recovered')}
          accentColor="var(--accent-teal)"
          data={accounts && `₹ ${totalInvested.toLocaleString('en-IN', { maximumFractionDigits: 3 })}`}
          subdata={accounts && `₹ ${Math.round(totalProfitCollected).toLocaleString('en-IN', { maximumFractionDigits: 3 })} ${t('dashboard.interest_recovered')}`}
        />
        <DashboardCard
          title={t('dashboard.overall_recovered')}
          subheader={t('dashboard.total_recovered_all_time')}
          accentColor="var(--accent-green)"
          data={accounts && `₹ ${(totalReturned + totalUpfrontReturned).toLocaleString('en-IN', { maximumFractionDigits: 3 })}`}
          subdata={accounts && (totalUpfrontReturned > 0 ? `₹ ${totalReturned.toLocaleString('en-IN', { maximumFractionDigits: 3 })} ${t('dashboard.installs')} + ₹ ${totalUpfrontReturned.toLocaleString('en-IN', { maximumFractionDigits: 3 })} ${t('dashboard.upfront')}` : t('dashboard.total_collections'))}
        />
      </div>

      <div className="dashboard-cards-grid" style={{ marginTop: 16 }}>
        <DashboardCard
          title={t('dashboard.daily_accounts')}
          subheader={`${modeAnalytics.daily.c} ${t('dashboard.active')}`}
          accentColor="var(--accent-yellow)"
          data={`₹ ${modeAnalytics.daily.est.toLocaleString('en-IN', { maximumFractionDigits: 3 })}`}
          subdata={t('dashboard.todays_est_collection')}
        />
        <DashboardCard
          title={t('dashboard.weekly_accounts')}
          subheader={`${modeAnalytics.weekly.c} ${t('dashboard.active')}`}
          accentColor="var(--accent-teal)"
          data={`₹ ${modeAnalytics.weekly.est.toLocaleString('en-IN', { maximumFractionDigits: 3 })}`}
          subdata={t('dashboard.weekly_est_collection')}
        />
        <DashboardCard
          title={t('dashboard.monthly_accounts')}
          subheader={`${modeAnalytics.monthly.c} ${t('dashboard.active')}`}
          accentColor="var(--accent-blue)"
          data={`₹ ${modeAnalytics.monthly.est.toLocaleString('en-IN', { maximumFractionDigits: 3 })}`}
          subdata={t('dashboard.monthly_est_collection')}
        />
        <DashboardCard
          title="Not Tallied"
          subheader="Needs Review"
          accentColor="var(--accent-red)"
          data={modeAnalytics.notTallied.toString()}
          subdata="Discrepancies found"
        />
      </div>

      {/* ── Analytics Trends ───────────────────────── */}
      <div style={{ padding: '24px 4px 8px' }}>
         <h3 style={{ fontSize: 18, color: 'var(--text-primary)', margin: 0, fontWeight: 600 }}>Analytical Trends</h3>
         <Typography sx={{ color: 'var(--text-secondary)', fontSize: 13, mb: 3 }}>Portfolio tracking and insights</Typography>

         <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
           
           {/* Chart Row 1 */}
           <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 3 }}>
             
             {/* Main Chart: Estimated vs Actual */}
             <Paper sx={{ p: 3, background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 4 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', mb: 2 }}>Current Month Collections (Est. vs Actual)</Typography>
                <Box sx={{ width: '100%', height: 300 }}>
                  <ResponsiveContainer>
                    <LineChart data={monthlyTrackingData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                      <XAxis dataKey="day" stroke="var(--text-muted)" fontSize={12} tickLine={false} />
                      <YAxis stroke="var(--text-muted)" fontSize={12} tickLine={false} tickFormatter={(v)=> `₹${v/1000}k`} />
                      <RechartsTooltip 
                         contentStyle={{ backgroundColor: '#111827', borderColor: 'var(--border-subtle)', borderRadius: 8, color: '#fff' }}
                         itemStyle={{ fontSize: 13 }} labelStyle={{ color: 'var(--text-muted)' }}
                      />
                      <Legend wrapperStyle={{ fontSize: 12, paddingTop: 10 }} />
                      <Line type="monotone" dataKey="Estimated" stroke="#8884d8" strokeDasharray="5 5" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="Actual" stroke="#10b981" strokeWidth={3} dot={{ r: 3, fill: '#10b981' }} activeDot={{ r: 6 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
             </Paper>

             {/* Side Chart: Distribution */}
             <Paper sx={{ p: 3, background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 4 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', mb: 2 }}>Portfolio Distribution</Typography>
                <Box sx={{ width: '100%', height: 260 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={portfolioDistribution} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value">
                        {portfolioDistribution.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip contentStyle={{ backgroundColor: '#111827', borderColor: 'var(--border-subtle)', borderRadius: 8 }} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
             </Paper>
           </Box>

           {/* Chart Row 2 */}
           <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 3 }}>
             
             {/* Recovery Bar Chart */}
             <Paper sx={{ p: 3, background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 4 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', mb: 2 }}>Total Invested vs Recovered</Typography>
                <Box sx={{ width: '100%', height: 250 }}>
                  <ResponsiveContainer>
                    <BarChart data={recoveryData} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" horizontal={false} />
                      <XAxis type="number" stroke="var(--text-muted)" fontSize={12} tickFormatter={(v)=> `₹${v/1000}k`} />
                      <YAxis dataKey="name" type="category" stroke="var(--text-muted)" fontSize={12} width={100} />
                      <RechartsTooltip cursor={{fill: 'rgba(255,255,255,0.02)'}} contentStyle={{ backgroundColor: '#111827', borderColor: 'var(--border-subtle)', borderRadius: 8 }} />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Bar dataKey="Invested" fill="#15C358" radius={[0, 4, 4, 0]} barSize={32} />
                      <Bar dataKey="Recovered" fill="#10b981" radius={[0, 4, 4, 0]} barSize={32} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
             </Paper>

             {/* Top Defaulters List */}
             <Paper sx={{ p: 0, background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 4, overflow: 'hidden' }}>
                <Box sx={{ p: 3, borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarningAmber sx={{ color: '#ef4444' }} />
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>Top Defaulters</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  {topDefaulters.length > 0 ? topDefaulters.map((def, idx) => (
                    <Link key={idx} to={`/account/${def.accountId}`} style={{ textDecoration: 'none' }}>
                      <Box sx={{ 
                        p: 2, px: 3, 
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        borderBottom: '1px solid rgba(255,255,255,0.03)',
                        transition: 'background 0.2s ease',
                        '&:hover': { background: 'var(--bg-elevated)' }
                      }}>
                        <Box>
                          <Typography sx={{ color: 'var(--text-primary)', fontSize: 14, fontWeight: 500 }}>{def.name}</Typography>
                          <Typography sx={{ color: 'var(--text-muted)', fontSize: 12 }}>{def.accountId.split('_')[0]}</Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography sx={{ color: '#ef4444', fontSize: 14, fontWeight: 600 }}>{def.unpaidPeriods} periods missed</Typography>
                          <Typography sx={{ color: 'var(--text-muted)', fontSize: 12 }}>{def.installmentType}</Typography>
                        </Box>
                      </Box>
                    </Link>
                  )) : (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                      <Typography sx={{ color: 'var(--text-muted)', fontSize: 13 }}>No significant defaulters found.</Typography>
                    </Box>
                  )}
                </Box>
             </Paper>

           </Box>

         </Box>
      </div>

      </PremiumFeatureLock>
    </div>
  );
}

export default Dashboard;

const currencyFormat = (value) => value.toLocaleString('en-IN', { maximumFractionDigits: 3 });

export const DashboardCard = ({ title, subheader, data, subdata, gradient, accentColor, style }) => (
  <div
    className="snap-item"
    style={{
      background: 'var(--bg-card)',
      border: `1px solid ${accentColor ? accentColor + '40' : 'var(--border-subtle)'}`,
      boxShadow: 'var(--shadow-md)',
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      cursor: 'default',
      position: 'relative',
      overflow: 'hidden',
      ...style,
    }}
  >
    {/* Accent glow blob */}
    <div style={{
      position: 'absolute', top: -20, right: -20,
      width: 80, height: 80,
      borderRadius: '50%',
      background: accentColor || '#15C358',
      opacity: 0.12,
      filter: 'blur(20px)',
      pointerEvents: 'none',
    }} />

    <p style={{
      fontSize: 11, fontWeight: 600, letterSpacing: '0.08em',
      textTransform: 'uppercase', color: accentColor || 'var(--text-secondary)',
      marginBottom: 6, fontFamily: 'Inter, sans-serif',
    }}>{title}</p>

    {subheader && (
      <p style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 14, fontFamily: 'Inter, sans-serif' }}>
        {subheader}
      </p>
    )}

    <p style={{
      fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em',
      color: 'var(--text-primary)', fontFamily: 'Inter, sans-serif', marginBottom: 4,
    }}>
      {data ?? '—'}
    </p>

    {subdata && (
      <p style={{ fontSize: 12, color: accentColor || 'var(--text-secondary)', fontFamily: 'Inter, sans-serif', fontWeight: 500 }}>
        {subdata}
      </p>
    )}
  </div>
);
